/**
 * OnboardingBanner — slim site-wide banner shown when the current user has
 * incomplete REQUIRED onboarding steps. Hidden on /onboarding itself.
 *
 * Only triggers a strict redirect when nothing required is done yet (e.g.
 * brand new user) — once they've made some progress they keep navigating
 * with the banner as a constant nudge.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingBanner() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || pathname.startsWith('/onboarding') || pathname === '/login') return;
    axios.get(`${API}/onboarding/me`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {});
  }, [user, pathname]);

  if (!user || dismissed) return null;
  if (pathname.startsWith('/onboarding') || pathname === '/login') return null;
  if (!data || data.required_complete) return null;

  // Show a slim banner; only show even slimmer "% complete" when partly through
  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/40"
      data-testid="onboarding-banner">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-2 text-xs flex-wrap">
        <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
          <Sparkles className="w-3.5 h-3.5 shrink-0" />
          <span><strong>Welcome aboard!</strong> Your DIAS-required onboarding is {data.required_percent}% complete. A few quick steps left:</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/onboarding" data-testid="onboarding-banner-link"
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-md text-white bg-amber-600 hover:bg-amber-700">
            Finish onboarding <ArrowRight className="w-3 h-3" />
          </Link>
          <button onClick={() => setDismissed(true)} aria-label="Dismiss"
            data-testid="onboarding-banner-dismiss" className="text-amber-800 dark:text-amber-300 hover:opacity-70">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
